import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    LogOut,
    Users,
    Handshake,
    Mail,
    Inbox,
    Search,
    Download,
} from "lucide-react";
import {
    adminGetToken,
    adminClearToken,
    adminSummary,
    adminApplications,
    adminPartnerships,
    adminContacts,
} from "@/lib/admin";

const TABS = [
    { key: "applications", label: "Applications", icon: Users },
    { key: "partnerships", label: "Partnerships", icon: Handshake },
    { key: "contacts", label: "Contacts", icon: Mail },
];

const formatDate = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch {
        return iso;
    }
};

const downloadCSV = (rows, filename) => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const csv = [
        headers.join(","),
        ...rows.map((r) =>
            headers
                .map((h) => {
                    const v = r[h];
                    if (v === null || v === undefined) return "";
                    const s = Array.isArray(v) ? v.join(";") : String(v);
                    return `"${s.replace(/"/g, '""')}"`;
                })
                .join(","),
        ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

export default function AdminDashboard() {
    const nav = useNavigate();
    const [tab, setTab] = useState("applications");
    const [summary, setSummary] = useState(null);
    const [data, setData] = useState({
        applications: null,
        partnerships: null,
        contacts: null,
    });
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);

    const hasToken = !!adminGetToken();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, a, p, c] = await Promise.all([
                adminSummary(),
                adminApplications(),
                adminPartnerships(),
                adminContacts(),
            ]);
            setSummary(s.data);
            setData({
                applications: a.data,
                partnerships: p.data,
                contacts: c.data,
            });
        } catch (err) {
            if (err?.response?.status === 401) {
                adminClearToken();
                nav("/admin/login", { replace: true });
            }
        } finally {
            setLoading(false);
        }
    }, [nav]);

    useEffect(() => {
        if (hasToken) load();
    }, [hasToken, load]);

    if (!hasToken) {
        return <Navigate to="/admin/login" replace />;
    }

    const logout = () => {
        adminClearToken();
        nav("/admin/login", { replace: true });
    };

    const currentRows = (data[tab] || []).filter((row) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return JSON.stringify(row).toLowerCase().includes(q);
    });

    return (
        <div className="min-h-screen bg-[#050505] text-white">
            {/* Top bar */}
            <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 grid place-items-center border border-[#D4AF37]">
                            <span className="font-display text-base text-[#D4AF37]">
                                YM
                            </span>
                        </div>
                        <div className="leading-none">
                            <div className="font-display text-sm uppercase tracking-wider text-white">
                                Admin Console
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-1">
                                EDUtainment Pros · YMP
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href="/"
                            className="hidden sm:inline-flex text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#D4AF37] px-3 py-2"
                        >
                            View Site
                        </a>
                        <button
                            data-testid="admin-logout"
                            onClick={logout}
                            className="flex items-center gap-2 border border-white/10 hover:border-[#D4AF37]/50 px-3 py-2 text-xs uppercase tracking-[0.25em] text-white/80 hover:text-[#D4AF37]"
                        >
                            <LogOut size={14} />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
                <div className="mb-10">
                    <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight leading-[0.95]">
                        Inbox
                    </h1>
                    <p className="text-white/55 mt-2 text-sm">
                        Submissions from across the site, freshest first.
                    </p>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#D4AF37]/20 border border-[#D4AF37]/20 mb-10">
                    {[
                        { k: "applications", l: "Applications", icon: Users },
                        { k: "partnerships", l: "Partnerships", icon: Handshake },
                        { k: "contacts", l: "Contacts", icon: Mail },
                        { k: "newsletter", l: "Newsletter", icon: Inbox },
                    ].map((s) => (
                        <motion.div
                            key={s.k}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-[#0A0A0A] p-6 relative"
                            data-testid={`admin-stat-${s.k}`}
                        >
                            <s.icon
                                className="text-[#D4AF37]/30 absolute top-4 right-4"
                                size={20}
                            />
                            <div className="font-accent text-5xl text-[#D4AF37] leading-none">
                                {summary ? summary[s.k] : "—"}
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.25em] text-white/55 mt-3">
                                {s.l}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 border-b border-white/10 mb-6 overflow-x-auto no-scrollbar">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            data-testid={`admin-tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.25em] border-b-2 transition-all whitespace-nowrap ${
                                tab === t.key
                                    ? "border-[#D4AF37] text-[#D4AF37]"
                                    : "border-transparent text-white/50 hover:text-white"
                            }`}
                        >
                            <t.icon size={14} />
                            {t.label}
                            <span className="text-[10px] text-white/40 ml-1">
                                ({data[t.key]?.length ?? 0})
                            </span>
                        </button>
                    ))}
                </div>

                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-5">
                    <div className="flex items-center flex-1 border border-white/10 px-3 bg-black/40 focus-within:border-[#D4AF37]">
                        <Search size={14} className="text-white/40" />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={`Search ${tab}...`}
                            data-testid="admin-search"
                            className="bg-transparent px-3 py-2.5 text-sm outline-none w-full placeholder:text-white/30"
                        />
                    </div>
                    <button
                        onClick={() =>
                            downloadCSV(currentRows, `${tab}-${Date.now()}.csv`)
                        }
                        data-testid="admin-export-csv"
                        disabled={!currentRows.length}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 border border-white/15 hover:border-[#D4AF37] text-xs uppercase tracking-[0.25em] text-white/75 hover:text-[#D4AF37] disabled:opacity-40"
                    >
                        <Download size={14} />
                        Export CSV
                    </button>
                    <button
                        onClick={load}
                        data-testid="admin-refresh"
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.25em] hover:bg-[#FFF3A1]"
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </button>
                </div>

                {/* Table */}
                <div
                    className="border border-white/10 overflow-hidden"
                    data-testid={`admin-table-${tab}`}
                >
                    {currentRows.length === 0 ? (
                        <div className="p-16 text-center text-white/40 text-sm">
                            {(() => {
                        if (loading) return "Loading...";
                        if (query) return "No matches for that search.";
                        return `No ${tab} yet. They'll appear here as visitors submit forms.`;
                    })()}
                        </div>
                    ) : (
                        <div className="divide-y divide-white/5">
                            {currentRows.map((row) => (
                                <RowCard key={row.id} row={row} kind={tab} />
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

const ROW_PRIMARY = {
    applications: (r) => r.full_name,
    partnerships: (r) => r.organization,
    contacts: (r) => r.name,
};

const ROW_SECONDARY = {
    applications: (r) =>
        `${r.email} · ${r.phone}${r.age ? ` · age ${r.age}` : ""}`,
    partnerships: (r) =>
        `${r.contact_name} · ${r.email} · ${r.partnership_type}`,
    contacts: (r) => `${r.email}${r.subject ? ` · ${r.subject}` : ""}`,
};

function RowCard({ row, kind }) {
    const [expanded, setExpanded] = useState(false);

    const primary = ROW_PRIMARY[kind]?.(row);
    const secondary = ROW_SECONDARY[kind]?.(row);

    return (
        <div className="p-5 hover:bg-white/[0.02] transition-colors">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full text-left flex items-start justify-between gap-4"
            >
                <div className="flex-1 min-w-0">
                    <div className="font-display text-lg uppercase tracking-tight text-white truncate">
                        {primary || "—"}
                    </div>
                    <div className="text-xs text-white/55 mt-1 truncate">
                        {secondary}
                    </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 whitespace-nowrap shrink-0 pt-1">
                    {formatDate(row.created_at)}
                </div>
            </button>
            {expanded && (
                <div className="mt-4 pt-4 border-t border-white/5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {Object.entries(row)
                        .filter(([k]) => k !== "id" && k !== "_id")
                        .map(([k, v]) => (
                            <div key={k} className="flex gap-3">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] min-w-[120px] pt-1">
                                    {k.replace(/_/g, " ")}
                                </div>
                                <div className="text-white/85 break-words flex-1">
                                    {Array.isArray(v)
                                        ? v.join(", ") || "—"
                                        : String(v ?? "—")}
                                </div>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
}
